import { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import { SxProps, Theme } from '@mui/material/styles';
import { Account } from '../../models';
import { getAccountAvatar, getInitials } from '../../utils/Users';

interface AccountAvatarProps {
  account: Pick<Account, 'name' | 'logo'>;
  size?: number;
  id?: string;
  className?: string;
  sx?: SxProps<Theme>;
}

/**
 * Account avatar in sync with TestOps: shows the account's custom logo when
 * one is set, otherwise falls back to the initials of the account name
 * (same default TestOps uses). MUI Avatar also renders the fallback when the
 * image fails to load.
 *
 * Kat One uploads the logo under a stable key (`<accountUuid>.<ext>`) with a
 * short `Cache-Control`, so — like TestOps G3 — a per-mount cache-buster is
 * appended to make sure a logo changed in TestOps shows up on next load.
 */
export const AccountAvatar = ({ account, size = 32, id, className, sx }: AccountAvatarProps) => {
  const initials = getInitials(account.name);
  const src = useMemo(() => {
    const resolved = getAccountAvatar(account.logo);
    if (!resolved || resolved.startsWith('data:')) {
      return resolved;
    }
    return `${resolved}${resolved.includes('?') ? '&' : '?'}t=${Date.now()}`;
  }, [account.logo]);

  return (
    <Avatar
      id={id}
      className={className}
      src={src || undefined}
      alt={account.name}
      sx={{
        width: size,
        height: size,
        backgroundColor: '#0f8461',
        color: '#fff',
        fontSize: Math.round(size * 0.38),
        fontWeight: 700,
        letterSpacing: '.02em',
        flex: 'none',
        ...sx,
      }}
    >
      {initials || undefined}
    </Avatar>
  );
};
